/**
 * Which link in the nav is "the page you are on", and how this template says so.
 *
 * ## The two defects this exists to fix
 *
 * 1. **The builder canvas never marked an active link at all.** Only the
 *    exporters did. What you saw on the canvas was whatever the CRAWLER froze —
 *    herocaredemo's nav ships with `id="active-menu"` on Home, so Home stayed
 *    highlighted on every page forever.
 * 2. **Both exporters emitted `cs-active` and nothing else.** Measured over the
 *    197 crawled navs, 12 templates mark the current page with something else
 *    (7 `.active`, 4 `#active-menu`, 1 `#active`). On those the exported site
 *    got a class its own stylesheet never styles, so the highlight silently
 *    vanished.
 *
 * ## Why the convention is derived at RUNTIME, not at parse time
 *
 * The obvious design is a parser sidecar next to `_font-policy.json`. It was
 * rejected: it would mean re-running `build-catalogs.js` — the most destructive
 * script in the repo, which has wiped template data twice — across 197
 * templates, plus an R2 upload, to learn something already sitting in the nav
 * section that the project has loaded.
 *
 * The crawler froze the source page's own active state into the markup. That
 * frozen marker IS the convention: exactly one link wears a token, the nav's own
 * CSS styles that token, and the token's NAME says it means "current". All three
 * conditions are checked, because "worn by exactly one link" alone is not
 * enough — the logo link is also unique, which is why `.cs-logo` came back as a
 * false positive on 9 templates before the name test was added.
 *
 * Measured over all 197 navs: 163 yield a convention, 0 false positives. The
 * remaining 34 froze no marker and fall back to `cs-active`, which is what they
 * got before — so this is additive and no template regresses. It also means a
 * project that mixes templates reads the nav it ACTUALLY has, which a
 * per-template sidecar keyed on the primary domain would get wrong.
 */

import * as csstree from '../vendor/csstree.esm.js';

/** What 170 of 197 templates use, and the fallback when nothing is derivable. */
export const DEFAULT_NAV_ACTIVE_CONVENTION = Object.freeze({ kind: 'class', token: 'cs-active' });

/**
 * A token that means "this is the current page" rather than something
 * structural. `.cs-logo` and `.cs-desktop` are each worn by exactly one link
 * too; only the name separates them from a real marker.
 */
const ACTIVE_TOKEN_RE = /(^|[-_])(active|current|selected)([-_]|$)/i;

function tokensInCss(css) {
  const classes = new Set();
  const ids = new Set();
  try {
    const ast = csstree.parse(String(css || ''), {
      parseValue: false,
      parseAtrulePrelude: false
    });
    csstree.walk(ast, {
      enter(node) {
        if (node.type === 'ClassSelector') classes.add(node.name);
        else if (node.type === 'IdSelector') ids.add(node.name);
      }
    });
  } catch {
    // Unparsed CSS is no evidence. The caller falls back to the default
    // convention rather than guessing from the markup alone.
  }
  return { classes, ids };
}

/**
 * Derive the active-link convention from a nav section.
 *
 * @param {{html?: string, css?: string}} navSection
 * @returns {{kind: 'class'|'id', token: string}} never null — falls back to the
 *   CodeStitch default, which is what these templates got before this existed.
 */
export function deriveNavActiveConvention(navSection) {
  const html = typeof navSection?.html === 'string' ? navSection.html : '';
  const css = typeof navSection?.css === 'string' ? navSection.css : '';
  if (!html) return DEFAULT_NAV_ACTIVE_CONVENTION;

  const links = [];
  const linkRe = /<a\b([^>]*)>/gi;
  let match;
  while ((match = linkRe.exec(html))) {
    const attrs = match[1];
    const classAttr = /\bclass="([^"]*)"/i.exec(attrs);
    const idAttr = /\bid="([^"]*)"/i.exec(attrs);
    links.push({
      classes: classAttr ? classAttr[1].split(/\s+/).filter(Boolean) : [],
      id: idAttr ? idAttr[1].trim() : ''
    });
  }
  if (links.length < 2) return DEFAULT_NAV_ACTIVE_CONVENTION;

  const { classes: cssClasses, ids: cssIds } = tokensInCss(css);

  // An id is unique by definition, so it needs no uniqueness test — only that
  // the nav's CSS styles it and that its name means "current".
  for (const link of links) {
    if (!link.id) continue;
    if (!cssIds.has(link.id)) continue;
    if (!ACTIVE_TOKEN_RE.test(link.id)) continue;
    return { kind: 'id', token: link.id };
  }

  const classCounts = new Map();
  for (const link of links) {
    for (const name of new Set(link.classes)) {
      classCounts.set(name, (classCounts.get(name) || 0) + 1);
    }
  }
  for (const [name, count] of classCounts) {
    if (count !== 1) continue; // a per-page state is on exactly one link
    if (!cssClasses.has(name)) continue;
    if (!ACTIVE_TOKEN_RE.test(name)) continue;
    return { kind: 'class', token: name };
  }

  return DEFAULT_NAV_ACTIVE_CONVENTION;
}

/**
 * Does this link point at this page? Text-first, matching the behaviour the
 * flat exporter has always had, so moving both paths onto this function cannot
 * change which link gets marked on a template that already worked.
 */
function linkMatchesPage(linkText, page) {
  const text = String(linkText || '')
    .trim()
    .toLowerCase();
  if (!text) return false;
  const pageName = String(page?.name || '')
    .trim()
    .toLowerCase();
  if (!pageName) return false;
  return text === pageName || text.includes(pageName) || pageName.includes(text);
}

/**
 * Clear every frozen marker from the nav and set it on the current page's link.
 *
 * Clearing FIRST is the half the old exporter got wrong: it only touched links
 * it had matched to a page, so a link the crawler froze as active that no
 * longer matches any page kept its marker and the exported site highlighted two
 * items — or the wrong one.
 *
 * Href rewriting is deliberately NOT done here — the flat exporter owns that,
 * and it needs the page list this function does not.
 *
 * @param {Element} navRoot   a parsed nav subtree (mutated in place)
 * @param {object}  currentPage
 * @param {{kind:string, token:string}} convention
 * @returns {boolean} whether a link was marked
 */
export function applyNavActiveConvention(navRoot, currentPage, convention) {
  if (!navRoot || !convention?.token) return false;
  const { kind, token } = convention;
  const links = Array.from(navRoot.querySelectorAll('a'));
  if (!links.length) return false;

  // 1. Clear the frozen state everywhere, including the CodeStitch default —
  //    a template can carry both its own marker and a stale `cs-active`.
  for (const link of links) {
    link.classList.remove(token);
    link.classList.remove(DEFAULT_NAV_ACTIVE_CONVENTION.token);
    link.removeAttribute('aria-current');
    if (kind === 'id' && link.getAttribute('id') === token) link.removeAttribute('id');
  }

  // 2. Mark the current page's link. Uses the same scorer as the 11ty path so
  //    a nav containing both "Services" and "Service Areas" resolves to the
  //    same link in the canvas, the saved preview and every export.
  const index = findActiveLinkIndex(
    links.map((link) => link.textContent),
    currentPage
  );
  if (index < 0) return false;

  const target = links[index];
  if (kind === 'id') target.setAttribute('id', token);
  else target.classList.add(token);
  target.setAttribute('aria-current', 'page');
  return true;
}

/**
 * Best-matching link index for a page, used where a caller needs the decision
 * without mutating. Exported so the 11ty path — which cannot mutate per page,
 * because it emits ONE shared header include — can ask the same question.
 */
export function findActiveLinkIndex(linkTexts, currentPage) {
  let best = -1;
  let bestScore = 0;
  linkTexts.forEach((text, index) => {
    if (!linkMatchesPage(text, currentPage)) return;
    const t = String(text || '')
      .trim()
      .toLowerCase();
    const name = String(currentPage?.name || '')
      .trim()
      .toLowerCase();
    const score = t === name ? 3 : t.includes(name) ? 2 : 1;
    if (score > bestScore) {
      bestScore = score;
      best = index;
    }
  });
  return best;
}
